import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import { usePage } from "@hooks/usePage";
import SelectInput from "@commons/Input/Select";
import { GENDER_OPTIONS } from "@typings/entities/individuals";
import { SAMPLE_TYPE_OPTIONS } from "@typings/entities/samples/enums";
import { validateCoordinates } from "@utils/formatters";
import ComboBox from "@commons/Input/ComboBox";
import { searchTaxonomic } from "@services/taxonomicGroups/search";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import Map from "@commons/Map";
import PublicIcon from "@mui/icons-material/Public";
import Text from "@commons/Typography/Text";
import { createSample } from "@services/samples/create";
import { ComboBoxDefault } from "@typings/components/comboBox";
import { useModalOpenWithCoordinates } from "@hooks/useModalOpenWithCoordinates";
import { searchIndividuals } from "@services/individuals/search";
import { searchUsers } from "@services/users/search";
import { useEffect } from "react";
import { createAttachmentsSample } from "@services/samples/createAttachments";
import { config } from "@/config";
import UploadInput from "@commons/Input/Upload";
import { globalMutate } from "@utils/swr";
import { searchVisit } from "@services/visits/search";
import type { Visit } from "@typings/entities/visit";
import {
  sampleDefaultValues,
  sampleSchema,
  type SampleFormData,
} from "../../schemas/sampleSchema";

function ModalNewSample() {
  const { isModalOpen, closeModal, setIsLoading, setDataModal } = usePage();
  const open = isModalOpen("newSample");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<SampleFormData>({
    resolver: zodResolver(sampleSchema),
    defaultValues: sampleDefaultValues,
  });

  useModalOpenWithCoordinates<SampleFormData>({ open, setValue });

  const individual = watch("individual");
  const anonymousTaxonomicGroup = watch("anonymousIndividualTaxonomicGroup");
  const anonymousGender = watch("anonymousIndividualGender");

  const hasIndividual = !!individual?.value;
  const hasAnonymous = !!(anonymousTaxonomicGroup?.value || anonymousGender);

  const isAnonymousDisabled = hasIndividual;
  const isIndividualDisabled = hasAnonymous;

  useEffect(() => {
    if (isAnonymousDisabled) {
      setValue("anonymousIndividualTaxonomicGroup", ComboBoxDefault);
      setValue("anonymousIndividualGender", undefined);
    }
  }, [isAnonymousDisabled, setValue]);

  useEffect(() => {
    if (isIndividualDisabled) {
      setValue("individual", ComboBoxDefault);
    }
  }, [isIndividualDisabled, setValue]);

  const onSubmit = async (data: SampleFormData) => {
    try {
      setIsLoading(true);

      const sampleCreated = await createSample({
        reporterId: data.reporter.value,
        individualId: data.individual?.value || undefined,
        anonymousIndividualTaxonomicGroupId:
          data.anonymousIndividualTaxonomicGroup?.value || undefined,
        anonymousIndividualGender: data.anonymousIndividualGender,
        observations: data.observations,
        type: data.type,
        sampleDate: data.sampleDate,
        destination: data.destination,
        visitId: data.visit?.value || undefined,
        latitude: Number(data.latitude),
        longitude: Number(data.longitude),
      });

      if (data.attachments && data.attachments.length > 0) {
        await createAttachmentsSample({
          id: sampleCreated.sampleId,
          files: data.attachments || [],
        });
      }

      await globalMutate([`fetch-samples-geojson`, `fetch-get-samples`]);

      toast.success("Amostra criada com sucesso");

      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao criar amostra";
      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setDataModal({});
    closeModal();
  };

  if (!open) return null;

  const latitude = watch("latitude");
  const longitude = watch("longitude");

  const { hasValidCoordinates, lat, lng } = validateCoordinates(
    latitude,
    longitude,
  );

  return (
    <Modal
      open={open}
      heading="Cadastrar Amostra"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-4xl"
    >
      <form className="grid grid-cols-1 lg:grid-cols-2 gap-4 gap-x-6">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="latitude"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Latitude"
                  type="number"
                  textSize="sm"
                  fullWidth
                  error={!!errors.latitude}
                  helperText={errors.latitude?.message}
                />
              )}
            />
            <Controller
              name="longitude"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Longitude"
                  type="number"
                  fullWidth
                  error={!!errors.longitude}
                  helperText={errors.longitude?.message}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <SelectInput
                  {...field}
                  label="Tipo de Amostra"
                  options={SAMPLE_TYPE_OPTIONS}
                  size="small"
                  fullWidth
                  value={field.value || ""}
                  error={!!errors.type}
                  helperText={errors.type?.message}
                />
              )}
            />

            <Controller
              name="sampleDate"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Data e hora"
                  type="datetime-local"
                  fullWidth
                  error={!!errors.sampleDate}
                  helperText={errors.sampleDate?.message}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="reporter"
              control={control}
              render={({ field }) => (
                <ComboBox
                  {...field}
                  label="Reportado por:"
                  value={field.value ?? null}
                  onChange={(option) => field.onChange(option ?? null)}
                  searchService={searchUsers}
                  error={!!errors.reporter}
                  helperText={errors.reporter?.label?.message}
                />
              )}
            />

            <Controller
              name="visit"
              control={control}
              render={({ field }) => (
                <ComboBox<Visit>
                  {...field}
                  label="Visita:"
                  value={field.value ?? null}
                  onChange={(option) => field.onChange(option ?? null)}
                  searchService={searchVisit}
                  minSearchLength={0}
                  debounceTime={300}
                  error={!!errors.visit}
                  helperText={errors.visit?.label?.message}
                />
              )}
            />
          </div>
        </div>

        <div className="h-64 lg:h-full w-full order-first lg:order-none">
          {hasValidCoordinates ? (
            <Map.Root>
              <Map.Localization latitude={lat} longitude={lng} draw />
            </Map.Root>
          ) : (
            <div className="flex flex-col items-center justify-center h-full bg-slate-100 rounded-md shadow-sm gap-2 p-6 text-center">
              <PublicIcon className="text-4xl text-gray-400" />
              <Text size="sm">
                Insira coordenadas válidas para visualizar o mapa.
              </Text>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:col-span-2">
          <Controller
            name="destination"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Destino"
                fullWidth
                type="text"
                value={field.value || ""}
                error={!!errors.destination}
                helperText={errors.destination?.message}
              />
            )}
          />

          <div
            className={
              isIndividualDisabled ? "opacity-50 pointer-events-none" : ""
            }
          >
            <Controller
              name="individual"
              control={control}
              render={({ field }) => (
                <ComboBox
                  {...field}
                  label="Indivíduo"
                  value={field.value ?? null}
                  onChange={(option) => field.onChange(option ?? null)}
                  searchService={searchIndividuals}
                  error={!!errors.individual}
                  helperText={
                    errors.individual?.label?.message ||
                    (isIndividualDisabled
                      ? "Desabilitado quando outros dados estão preenchidos"
                      : "")
                  }
                  disabled={isIndividualDisabled}
                />
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:col-span-2">
          <div
            className={
              isAnonymousDisabled ? "opacity-50 pointer-events-none" : ""
            }
          >
            <Controller
              name="anonymousIndividualTaxonomicGroup"
              control={control}
              render={({ field }) => (
                <ComboBox
                  {...field}
                  label="Espécie"
                  value={field.value ?? null}
                  onChange={(option) => field.onChange(option ?? null)}
                  searchService={searchTaxonomic}
                  error={!!errors.anonymousIndividualTaxonomicGroup}
                  helperText={
                    errors.anonymousIndividualTaxonomicGroup?.label?.message ||
                    (isAnonymousDisabled
                      ? "Desabilitado quando indivíduo está selecionado"
                      : "")
                  }
                  disabled={isAnonymousDisabled}
                />
              )}
            />
          </div>

          <div
            className={
              isAnonymousDisabled ? "opacity-50 pointer-events-none" : ""
            }
          >
            <Controller
              name="anonymousIndividualGender"
              control={control}
              render={({ field }) => (
                <SelectInput
                  {...field}
                  label="Sexo"
                  options={GENDER_OPTIONS}
                  size="small"
                  fullWidth
                  value={field.value || ""}
                  error={!!errors.anonymousIndividualGender}
                  helperText={
                    errors.anonymousIndividualGender?.message ||
                    (isAnonymousDisabled
                      ? "Desabilitado quando indivíduo está selecionado"
                      : "")
                  }
                  disabled={isAnonymousDisabled}
                />
              )}
            />
          </div>
        </div>

        <div className="lg:col-span-2">
          <Controller
            name="observations"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Observações"
                textSize="sm"
                fullWidth
                multiline
                rows={3}
                error={!!errors.observations}
                helperText={errors.observations?.message}
              />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Controller
            name="attachments"
            control={control}
            render={({ field: { value, onChange, onBlur, name } }) => (
              <UploadInput
                value={value || []}
                onChange={onChange}
                onBlur={onBlur}
                name={name}
                multiple={config.uploadArchive.multiple}
                accept={config.uploadArchive.accept}
                maxFiles={config.uploadArchive.maxFiles}
              />
            )}
          />
          {errors.attachments && (
            <p className="text-red-500 text-sm mt-1">
              {errors.attachments.message}
            </p>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default ModalNewSample;
